import React, { useState } from 'react';
import { observer } from 'mobx-react';

import BaseDialog from 'core/components/base-dialog';
import { Button } from 'core/components/button';
import Checkbox from 'core/components/checkbox';
import { Label } from 'core/components/label';
import { NotificationCard } from 'core/components/notification-card';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store';
import infoDarkGreen from 'core/assets/svg/info-dark-green.svg';
import useStyles from './send-invoice.styles';

/**
 * Staff "Send Invoice" billing-tab action (#34203): send the policy's current invoice to the
 * INSURED via the chosen channel(s) — email and/or a physical Lob letter. Nothing is sent to
 * lenders (LenderDock owns lender comms — #34004). A channel whose target is missing (no email /
 * no mailing address on file) is disabled with an explanatory note so an impossible send can't be
 * requested; when neither channel is possible the action is blocked entirely.
 */
const SendInvoice = observer(function SendInvoice({ className }) {
  const classes = useStyles();
  const toast = useToast();
  const {
    account: {
      email,
      policies: {
        policy: { policy, policyDetails, billingDetails, sendPolicyInvoice }
      }
    }
  } = useStore();

  const hasEmail = !!email?.trim();
  const hasMailingAddress = !!policyDetails?.global?.mailingAddress?.address?.trim();
  // Nothing to invoice when there's no outstanding balance (e.g. a fully-paid policy). Guards against
  // creating a bogus $0 invoice on the backend's create path (#34203).
  const hasBalanceDue = Number(billingDetails?.totalRemaining) > 0;

  const [open, setOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState(hasEmail);
  const [sendLetter, setSendLetter] = useState(false);
  const [sending, setSending] = useState(false);

  const openModal = () => {
    // Default to the channels that are actually possible.
    setSendEmail(hasEmail);
    setSendLetter(!hasEmail && hasMailingAddress);
    setOpen(true);
  };

  const canSend = hasBalanceDue && ((sendEmail && hasEmail) || (sendLetter && hasMailingAddress));

  const handleSend = async () => {
    if (!canSend) {
      return;
    }
    setSending(true);
    try {
      const res = await sendPolicyInvoice({ policyId: policy.id, sendEmail, sendLetter });
      if (res?.success) {
        // The letter channel is dropped server-side if one was mailed to this policy in the last
        // 7 days (#34203); tell staff the letter was skipped so they aren't surprised.
        toast.notify(
          res.letterSkippedRecentlyMailed
            ? {
                type: 'info',
                message:
                  'A letter was already mailed to this policy in the last 7 days, so the letter was skipped. The email was sent.',
                durationInSeconds: 10
              }
            : { type: 'success', message: 'Invoice send requested. It can take a few minutes to be delivered.' }
        );
        setOpen(false);
      } else {
        toast.notify({ type: 'error', message: res?.error || 'Could not send the invoice.' });
      }
    } catch (error) {
      toast.notify({ type: 'error', message: `An error occurred. ${error.message.replace(':', '').trim()}` });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Rendered as a text link under "Add a payment hold"; the caller passes that link's class
          (payment.styles.js `holdPaymentButton`) so the two can't drift apart. */}
      <Button variant="text" color="secondary" className={className} onClick={openModal}>
        Send Invoice
      </Button>
      <BaseDialog open={open} onClose={() => setOpen(false)} className={classes.dialog}>
        <div className={classes.content}>
          <Label type="h1" className={classes.title}>
            Send invoice to the insured
          </Label>
          <Label type="body3" className={classes.subtitle}>
            Choose how to deliver the policy’s current invoice. The invoice is only sent to the insured.
          </Label>

          <div className={classes.options}>
            <Checkbox
              id="send-invoice-email"
              mode="light"
              label={hasEmail ? 'Email' : 'Email — no email address on file'}
              value={sendEmail}
              disabled={!hasEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
            />
            <Checkbox
              id="send-invoice-letter"
              mode="light"
              label={hasMailingAddress ? 'Letter (mailed)' : 'Letter — no mailing address on file'}
              value={sendLetter}
              disabled={!hasMailingAddress}
              onChange={(e) => setSendLetter(e.target.checked)}
            />
          </div>

          {!hasBalanceDue && (
            <NotificationCard className={classes.notification} type="light" icon={infoDarkGreen} iconSize="icon16">
              <Label type="body3">
                There is no balance currently due on this policy, so there is nothing to invoice.
              </Label>
            </NotificationCard>
          )}

          {hasBalanceDue && !hasEmail && !hasMailingAddress && (
            <NotificationCard className={classes.notification} type="light" icon={infoDarkGreen} iconSize="icon16">
              <Label type="body3">
                This policy has neither an email address nor a mailing address on file, so the invoice can’t be sent.
                Add contact details first.
              </Label>
            </NotificationCard>
          )}

          <div className={classes.actions}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSend}
              disabled={!canSend || sending}
              loading={sending}
            >
              Send
            </Button>
          </div>
        </div>
      </BaseDialog>
    </>
  );
});

export default SendInvoice;
